/**
 * Pinia Persistence Plugin
 *
 * Simple localStorage persistence for Pinia stores
 */
import type { PiniaPluginContext } from 'pinia';

export interface PersistOptions {
  persist?: boolean | { key?: string; storage?: Storage; paths?: string[] };
}

export function createPersistedState() {
  return ({ options, store }: PiniaPluginContext) => {
    const opts = options as PersistOptions;
    const { persist } = opts;

    if (!persist) return;

    const persistConfig = typeof persist === 'object' ? persist : {};
    const storageKey = persistConfig.key ?? `pinia-${store.$id}`;
    const storage = persistConfig.storage ?? localStorage;
    const paths = persistConfig.paths;

    // Load persisted state
    const savedState = storage.getItem(storageKey);
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        store.$patch(paths ? pick(parsed, paths) : parsed);
        // PHX-1180: one-time migration — immediately rewrite storage with only the
        // allow-listed subset. Without this, fields dropped from `paths` (e.g. an
        // access/refresh token persisted by a previous app version) stay readable
        // in localStorage until the next mutation triggers the subscribe below.
        if (paths) {
          storage.setItem(storageKey, JSON.stringify(pick(parsed, paths)));
        }
      } catch (error) {
        console.error(`Failed to parse persisted state for ${store.$id}:`, error);
      }
    }

    // Subscribe to state changes and persist
    // flush: 'sync' ensures the localStorage write happens immediately, not on
    // the next tick — keeps the persisted snapshot consistent with in-memory
    // state at all times (no token is ever read from localStorage; auth is
    // cookie-based, see PHX-1180).
    store.$subscribe(
      (_mutation, state) => {
        try {
          const toPersist = paths ? pick(state, paths) : state;
          storage.setItem(storageKey, JSON.stringify(toPersist));
        } catch (error) {
          console.error(`Failed to persist state for ${store.$id}:`, error);
        }
      },
      { detached: true, flush: 'sync' }
    );
  };
}

function pick<T extends Record<string, unknown>>(source: T, paths: string[]): Partial<T> {
  const result: Record<string, unknown> = {};
  for (const path of paths) {
    const keys = path.split('.');
    let src: unknown = source;
    let dest: Record<string, unknown> = result;
    for (let i = 0; i < keys.length; i++) {
      const k = keys[i];
      if (src == null || typeof src !== 'object') break;
      if (i === keys.length - 1) {
        dest[k] = (src as Record<string, unknown>)[k];
      } else {
        dest[k] = dest[k] ?? {};
        dest = dest[k] as Record<string, unknown>;
        src = (src as Record<string, unknown>)[k];
      }
    }
  }
  return result as Partial<T>;
}
